import _ from 'lodash';
import Debug from '@/util/debug';
import moment from 'moment-timezone';
import { can } from '@/util/permissions';
import { STEP_TARGET, STEP_SETTINGS, STEP_CHECKOUT, STEP_PROPOSAL } from '@/services/StepService';
import { GOAL_REACH_AND_CLICK, GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_REACH_FREQUENCY, GOAL_POST_ENGAGEMENT } from '@/services/GoalTypeService';
import FormInput from '@/components/form-input/FormInput.vue';
import ReachInfo from '@/components/campaign-offer/ReachInfo';
import { fixPackageFormatFromApiMaf } from '@/services/PackageService';
import PackageSliderBox from '@/components/package-slider-box/PackageSliderBox.vue';
import SvgReachChart from '@/components/svg-reach-chart/SvgReachChart.vue';
import CorrectDatepicker from '@/components/correct-datepicker/CorrectDatepicker.vue';
import * as constants from '@/util/constants';
import { localDateToApi } from '@/util/helper';
import { getNextIterationStartDate, isIterationDate, getSubscriptionPeriodType, getSubscriptionPeriodLength } from '@/models/campaign';

const debug = Debug('edit-campaign');

export default {
  dependencies: ['adApiService', 'draftService', 'packageService', 'clusterService', 'genericConfigService', 'goalTypeService'],
  components: {
    FormInput,
    ReachInfo,
    PackageSliderBox,
    SvgReachChart,
    CorrectDatepicker,
  },
  props: {
    campaign: Object,
  },
  data() {
    return {
      newCampaign: null,
      loading: true,
      creating: false,
      error: false,
      optionsMap: {
        changeTarget: ['changeTarget'],
        upsellPrice: ['upsellPrice'],
        upsellAll: ['upsellAll'],
      },
      config: null,
      goalTypes: null,
      businessCategory: null,
    };
  },
  methods: {
    onChangeTarget() {
      this.$router.push({ name: STEP_TARGET, params: { id: this.$route.params.id } });
    },
    onUpsellPrice() {
      this.refreshNewCampaign().then(() => {
        this.newCampaign.start_date = this.minStart.toISOString();
        this.$refs.upsellPrice.show();
      });
    },
    onUpsellAll() {
      this.refreshNewCampaign().then(() => {
        this.createNewCampaign();
      });
    },
    createNewCampaign(lastStep = false) {
      this.creating = true;

      this.newCampaign.currentStep = lastStep ? STEP_CHECKOUT : STEP_SETTINGS;
      this.draftService.updateDraft('new', this.newCampaign)
        .then(id => {
          if (lastStep) {
            return this.draftService.publish(id)
              .then(data => data.draft_id)
              .then(this.$toast.success({ message: this.$t('pos.proposal.upsell-price-changed') }));
          }
          return id;
        })
        .then(id => {
          this.$router.push({
            name: lastStep ? STEP_PROPOSAL : STEP_TARGET,
            params: { id },
          });
        })
        .catch(e => {
          this.$toast.error({ message: e });
          this.creating = false;
        });
    },
    refreshNewCampaign() {
      return this.draftService.getNew()
        .then(draft => {
          const newCampaign = Object.assign({}, draft, _.cloneDeep(this.campaign));
          newCampaign.details.newcreative = 0;
          newCampaign.details['clone-of'] = _.get(this.campaign, 'famCampaign.code', null);
          newCampaign.details['clone-of-start-date'] = _.get(this.campaign, 'famCampaign.start_date', null);
          newCampaign.start_date = this.minStart ? this.minStart.toISOString() : null;
          this.newCampaign = newCampaign;
        });
    },
    offerByBudget(goalType) {
      return _.get(this.goalTypes, `${goalType}.offer_by`, 'REACH') === 'BUDGET';
    },
  },
  computed: {
    campaignStartDateMoment() {
      let startDate = moment(_.get(this.campaign, 'famCampaign.start_date', null));
      if (!startDate || !startDate.isValid()) {
        startDate = moment();
      }
      return startDate;
    },
    minStart() {
      return getNextIterationStartDate(
        this.campaignStartDateMoment,
        this.subscriptionPeriodType,
        this.subscriptionPeriodLength,
        moment().add(constants.CAMPAIGN_SUBSCRIPTION.minChangePriceInterval, 'days'));
    },
    subscriptionPeriodType() {
      return getSubscriptionPeriodType(this.config);
    },
    subscriptionPeriodLength() {
      return getSubscriptionPeriodLength(this.config);
    },
    options() {
      const options = {};
      const famCampaign = _.get(this.campaign, 'famCampaign', null);

      _.forOwn(this.optionsMap, (value, key) => {
        if (can(famCampaign, value)) {
          options[key] = true;
        }
      });

      return options;
    },
    availablePackages() {
      const cluster = this.clusterService.availableClusters(this.newCampaign)
        .find(c => c.packages.find(p => p.name === this.newCampaign.package));

      return _.get(cluster, 'packages', []);
    },
    packagesArr: function computedPackagesArr() {
      return this.availablePackages.map(p => ({
        name: p.name,
        reach: p.reach.min,
        price: Number(p.budget.budget),
      }));
    },
    selectedPackage: function computeSelectedPackage() {
      const result = this.availablePackages.find(p => p.name === this.newCampaign.package);
      return result || fixPackageFormatFromApiMaf({});
    },
    maxReach: function computedMaxReach() {
      return _.get(this.availablePackages, '[0].reach.min', 0);
    },
    disabledDates() {
      return {
        to: this.minStart,
        customPredictor: (date) => !isIterationDate(localDateToApi(date),
          this.subscriptionPeriodType, this.subscriptionPeriodLength,
          this.campaignStartDateMoment),
      };
    },
    isReachCampaign() {
      const goal = _.get(this.campaign, 'goal_type');
      return goal === GOAL_REACH_FREQUENCY || goal === GOAL_POST_ENGAGEMENT;
    },
    isLeadCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_LEAD_GENERATION;
    },
    isOfferCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_LOCAL_OFFERS;
    },
    isClicksCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_REACH_AND_CLICK;
    },
    audienceTitle() {
      let key = 'pos.offer.audience-guaranteed';

      if (this.offerByBudget(this.campaign.goal_type)) {
        key = 'pos.offer.campaign-budget';
      }

      return this.$t(key);
    },
    estimatedClicks() {
      const clickRes = {
        min: _.get(this.selectedPackage, 'clicks.min', 0),
        max: _.get(this.selectedPackage, 'clicks.max', 0),
      };
      const multi = parseFloat(_.get(this.businessCategory, 'clicks_multiplier', 0));
      if (!isNaN(multi) && multi > 0) {
        clickRes.min *= multi;
        clickRes.max *= multi;
      }
      return clickRes;
    },
    estimatedLeads() {
      return {
        min: _.get(this.selectedPackage, 'leads.min', 0),
        max: _.get(this.selectedPackage, 'leads.max', 0),
      };
    },
    estimatedOffers() {
      return {
        min: _.get(this.selectedPackage, 'offers.min', 0),
        max: _.get(this.selectedPackage, 'offers.max', 0),
      };
    },
  },
  mounted() {
    const fin = (err = null) => {
      debug.info('finished loading. error: %o', err);
      this.loading = false;
    };

    this.genericConfigService.getConfig().then(c => {
      this.config = c;
    });

    Promise.all([
      this.clusterService.getClusters(),
      this.goalTypeService.getOptions()
        .then(res => {
          this.goalTypes = res;
        }),
      Promise.resolve(this.campaign.business_category_id)
        .then((id) => {
          if (id) {
            return this.adApiService.getBusinessCategory(this.campaign.business_category_id);
          }
          return Promise.resolve(null);
        })
        .then(res => this.businessCategory = res)
        .catch(err => debug.warn(err)),
    ])
      .then(this.refreshNewCampaign)
      .then(fin)
      .catch(fin);
  },
};



// WEBPACK FOOTER //
// ./src/components/edit-campaign/EditCampaign.js